import { Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import {
  DesktopLicenseDetails,
  DesktopLicenseWithProduct,
  LicensesService,
  UpdateDesktopLicenseRequest,
} from 'src/app/services/licenses.service';
import { Tenant } from 'src/app/services/tenants.service';
import { ProductVariant } from 'src/app/services/products.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-edit-license',
  templateUrl: './edit-license.component.html',
  styleUrls: ['./edit-license.component.scss'],
})
export class EditLicenseComponent {
  @ViewChild('deleteLicenseDialog')
  deleteLicenseDialog!: ElementRef<HTMLDialogElement>;

  loadingLicense = false;
  updates!: UpdateDesktopLicenseRequest;
  originalLicense?: DesktopLicenseWithProduct;
  tenant?: Tenant;

  loadingPriceLockLevels = false;
  priceLockLevels: ProductVariant[] = [];
  errorFetchingPriceLockLevels = '';

  loadingAvailableParentLicenses = false;
  availableParentLicenses: DesktopLicenseDetails[] = [];
  errorFetchingAvailableParentLicenses = '';

  inputStartDate? = '';
  inputExpirationDate? = '';
  inputUniqueId = '';
  inputNotes = '';

  errorFetchingLicense = '';

  updating = false;
  errorUpdating = '';

  deletingLicense = false;

  constructor(
    public location: Location,
    private route: ActivatedRoute,
    private licensesService: LicensesService,
    private toastService: ToastService
  ) {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      console.error(new Error('No license ID found in route'));
      this.errorFetchingLicense = 'No license ID found';
      return;
    }
    this.getLicense(id);
    this.getPriceLockLevels(id);
  }

  getLicense(id: string) {
    this.loadingLicense = true;
    this.licensesService.getLicenseDetails(id).subscribe({
      next: (license) => {
        this.loadingLicense = false;
        this.originalLicense = license.desktop_license;
        this.tenant = license.tenant;
        this.initializeUpdates(this.originalLicense);
        this.getAvailableParentLicenses();
      },
      error: (err) => {
        this.loadingLicense = false;
        console.error(err);
        this.errorFetchingLicense = 'Error fetching license and tenant data';
      },
    });
  }

  getPriceLockLevels(licenseId: string) {
    this.loadingPriceLockLevels = true;
    this.licensesService.getPriceLockLevels(licenseId).subscribe({
      next: (res) => {
        console.log('Price lock levels: ', res);
        this.priceLockLevels = res;
        this.loadingPriceLockLevels = false;
      },
      error: (err) => {
        console.error(err);
        this.loadingPriceLockLevels = false;
        this.errorFetchingPriceLockLevels = 'Error fetching price lock levels';
      },
    });
  }

  getAvailableParentLicenses() {
    this.loadingAvailableParentLicenses = true;
    this.licensesService
      .getLicensesForTenant(this.originalLicense!.tenant_id)
      .subscribe({
        next: (res) => {
          this.loadingAvailableParentLicenses = false;
          this.availableParentLicenses = res.desktop_licenses.active.filter(
            (l) => l.id !== this.originalLicense!.id
          );
          console.log(
            'Available parent licenses: ',
            this.availableParentLicenses
          );
        },
        error: (err) => {
          console.error(err);
          this.errorFetchingAvailableParentLicenses =
            'Error fetching available parent licenses';
        },
      });
  }

  initializeUpdates(license: DesktopLicenseWithProduct) {
    this.updates = {
      active: license.active,
      site_license: license.site_license,
      renewal: license.renewal,
      expiration: license.expiration ? new Date(license.expiration) : undefined,
      start_date: license.start_date ? new Date(license.start_date) : undefined,
      transfers_left: license.transfers_left,
      will_renew: license.will_renew,
      new_license: license.new_license,
      price_lock_level: license.product_variant.price_lock_level,
      parent_desktop_license_id: license.parent_desktop_license_id ?? '',
      notes: license.notes ?? '',
    };

    console.log('initial updates: ', this.updates);

    this.inputNotes = license.notes ?? '';
    this.inputUniqueId = license.unique_id ?? '';
    this.inputStartDate = license.start_date?.slice(0, 10);
    this.inputExpirationDate = license.expiration?.slice(0, 10);
  }

  getLicenseType(license: DesktopLicenseWithProduct) {
    return this.licensesService.getType(license);
  }

  getLicensePrice(license: DesktopLicenseWithProduct) {
    return this.licensesService.getPrice(license);
  }

  getLockLevelPrice(level: ProductVariant) {
    if (!this.originalLicense) {
      console.error(new Error('No license to get lock level price'));
      return 0;
    }
    // add variant to license and get price
    const licenseWithVariant = {
      ...this.originalLicense!,
      product_variant: level,
    };
    return this.licensesService.getPrice(licenseWithVariant);
  }

  showConfirmDeleteDialog() {
    this.deleteLicenseDialog.nativeElement.showModal();
  }

  closeDeleteLicenseDialog() {
    this.deleteLicenseDialog.nativeElement.close();
  }

  deleteLicense() {
    if (!this.originalLicense) {
      console.error(new Error('No license to delete'));
      this.errorUpdating = 'No license set to delete';
      return;
    }

    this.deletingLicense = true;
    this.licensesService
      .deleteDesktopLicense(this.originalLicense!.id)
      .subscribe({
        next: (res) => {
          console.log('License deleted: ', res);
          this.deletingLicense = false;
          this.location.back();
        },
        error: (err) => {
          console.error(err);
          this.deletingLicense = false;
          this.errorUpdating = 'Unexpected error occurred. Please try again.';
        },
      });
  }

  get updateChanges() {
    if (!this.originalLicense) {
      return null;
    }
    const changes: UpdateDesktopLicenseRequest = {};

    if (this.updates.active !== this.originalLicense.active) {
      changes.active = this.updates.active;
    }

    if (this.updates.site_license !== this.originalLicense.site_license) {
      changes.site_license = this.updates.site_license;
    }

    if (this.updates.renewal !== this.originalLicense.renewal) {
      changes.renewal = this.updates.renewal;
    }

    if (
      this.inputExpirationDate !== this.originalLicense.expiration?.slice(0, 10)
    ) {
      changes.expiration = this.inputExpirationDate
        ? new Date(this.inputExpirationDate)
        : undefined;
    }

    if (this.inputStartDate !== this.originalLicense.start_date?.slice(0, 10)) {
      changes.start_date = this.inputStartDate
        ? new Date(this.inputStartDate)
        : undefined;
    }

    if (this.updates.transfers_left !== this.originalLicense.transfers_left) {
      changes.transfers_left = this.updates.transfers_left;
    }

    const origUniqueId = (this.originalLicense.unique_id ?? '').trim();
    if (this.inputUniqueId.trim() !== origUniqueId) {
      changes.unique_id = this.inputUniqueId.trim();
    }

    if (this.updates.will_renew !== this.originalLicense.will_renew) {
      changes.will_renew = this.updates.will_renew;
    }

    if (this.updates.new_license !== this.originalLicense.new_license) {
      changes.new_license = this.updates.new_license;
    }

    if (
      this.updates.price_lock_level !==
      this.originalLicense.product_variant.price_lock_level
    ) {
      changes.price_lock_level = this.updates.price_lock_level;
    }

    if (
      this.updates.parent_desktop_license_id !==
      this.originalLicense.parent_desktop_license_id
    ) {
      if (
        this.originalLicense.parent_desktop_license_id === undefined &&
        this.updates.parent_desktop_license_id === ''
      ) {
        // no change
      } else {
        changes.parent_desktop_license_id =
          this.updates.parent_desktop_license_id;
      }
    }

    const origNotes = this.originalLicense.notes ?? '';
    if (this.inputNotes !== origNotes) {
      changes.notes = this.inputNotes;
    }

    if (Object.keys(changes).length === 0) {
      return null;
    }
    return changes;
  }

  validateInputs() {
    if (!this.inputStartDate) {
      this.errorUpdating = 'Please enter a start date';
      return false;
    }

    if (!this.inputExpirationDate) {
      this.errorUpdating = 'Please enter an expiration date';
      return false;
    }

    const startDate = new Date(this.inputStartDate);
    const expirationDate = new Date(this.inputExpirationDate);
    if (startDate > expirationDate) {
      this.errorUpdating = 'Start date must be before expiration date';
      return false;
    }

    return true;
  }

  async updateLicense() {
    if (!this.originalLicense) {
      console.error(new Error('No license to update'));
      this.errorUpdating = 'No license to update';
      return;
    }

    this.updating = true;
    this.errorUpdating = '';

    const updates = this.updateChanges;

    if (!updates) {
      this.updating = false;
      this.errorUpdating = 'No changes to update';
      return;
    }

    if (!this.validateInputs()) {
      this.updating = false;
      return;
    }

    this.licensesService
      .updateLicense(this.originalLicense.id, updates)
      .subscribe({
        next: (res) => {
          console.log('License updated: ', res);

          if (this.originalLicense) {
            this.originalLicense = res.desktop_license;
            this.inputNotes = res.desktop_license.notes ?? '';
            this.inputUniqueId = res.desktop_license.unique_id ?? '';
          }
          this.updating = false;
          this.errorUpdating = '';
          this.toastService.addToast('success', 'License updated successfully');
        },
        error: (error) => {
          console.error(new Error(error));
          this.updating = false;
          this.errorUpdating = error;
          this.toastService.addToast('error', 'Error updating license');
        },
      });
  }

  goBack() {
    this.location.back();
  }

  cancelChanges() {
    this.location.back();
  }
}
